import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import AppRouter from './AppRouter';
import CookieConsent from './components/CookieConsent';
import ErrorBoundary from './components/ErrorBoundary';
import IntercomProvider from './components/IntercomProvider';
import { Auth0Provider } from '@auth0/auth0-react';
import { BrowserRouter } from 'react-router-dom';
import { initSentry } from './lib/sentry';
import { initWebVitals, checkAndReportRecoverySuccess } from './lib/analytics';
import { VersionProvider } from './contexts/VersionContext';
import { BookmarkProvider } from './contexts/BookmarkContext';
import { QuizHistoryProvider } from './contexts/QuizHistoryContext';
import UpdateBanner from './components/shared/UpdateBanner';

// Initialize Sentry for error tracking (before rendering)
initSentry();

// Only include audience if it's configured and not a placeholder
const auth0Audience = process.env.REACT_APP_AUTH0_AUDIENCE;
const authorizationParams = {
  redirect_uri: window.location.origin,
  // Only add audience if it exists and is not a placeholder value
  ...(auth0Audience && !auth0Audience.includes('your-') && { audience: auth0Audience })
};

/**
 * Auth0 redirect callback handler
 * Uses window.history.replaceState for reliable URL changes during Auth0 callback
 * This is critical for payment flow: after sign-up, user should return to
 * /?resumeCheckout=true so the pending Stripe checkout can be completed
 */
const onRedirectCallback = (appState) => {
  const returnTo = appState?.returnTo || window.location.pathname;

  // Use replaceState to update the URL without triggering a full page reload
  // This is more reliable than React Router's navigate during Auth0 callback
  window.history.replaceState({}, document.title, returnTo);

  // Dispatch popstate event to notify React Router of the URL change
  window.dispatchEvent(new PopStateEvent('popstate'));
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <Auth0Provider
          domain={(process.env.REACT_APP_AUTH0_DOMAIN || "YOUR_DOMAIN.auth0.com").trim()}
          clientId={(process.env.REACT_APP_AUTH0_CLIENT_ID || "YOUR_CLIENT_ID").trim()}
          authorizationParams={authorizationParams}
          cacheLocation="localstorage"
          useRefreshTokens={true}
          onRedirectCallback={onRedirectCallback}
        >
          <BookmarkProvider>
            <QuizHistoryProvider>
              <VersionProvider>
                <IntercomProvider>
                  <UpdateBanner position="top" />
                  <AppRouter />
                  <CookieConsent />
                </IntercomProvider>
              </VersionProvider>
            </QuizHistoryProvider>
          </BookmarkProvider>
        </Auth0Provider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);

// Initialize Core Web Vitals tracking after app renders
// This ensures accurate LCP and other metrics
initWebVitals();

// Check for pending error recovery and report success (HP-002)
// This tracks when users successfully recover from errors
checkAndReportRecoverySuccess();
